// Assignment Recommendation Utilities

import type { Employee, ResourceAllocation } from '@/types';
import type {
  PersonRecommendation,
  PersonAvailability,
  SkillMatch,
  ProjectConflict,
  AvailabilityStatus,
  ConflictLevel,
} from '@/types/assignment.types';

/**
 * Calculate person availability based on current allocations
 */
export function calculatePersonAvailability(
  person: Employee,
  allocations: ResourceAllocation[],
  projectStartDate: string,
  projectEndDate: string
): PersonAvailability {
  // Standard working hours per month (assuming 160 hours/month)
  const STANDARD_HOURS_PER_MONTH = 160;
  
  // Calculate project duration in months
  const start = new Date(projectStartDate);
  const end = new Date(projectEndDate);
  const monthsDuration = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)));
  
  const totalCapacity = STANDARD_HOURS_PER_MONTH * monthsDuration;
  
  // Find overlapping allocations
  const overlappingAllocations = allocations.filter(alloc => {
    const allocStart = new Date(alloc.startDate);
    const allocEnd = new Date(alloc.endDate);
    return alloc.employeeId === person.id && (
      (allocStart <= end && allocEnd >= start)
    );
  });
  
  // Calculate total allocated hours
  const allocatedHours = overlappingAllocations.reduce((sum, alloc) => {
    // Estimate hours based on allocation percent and overlap duration
    const overlapStart = new Date(Math.max(start.getTime(), new Date(alloc.startDate).getTime()));
    const overlapEnd = new Date(Math.min(end.getTime(), new Date(alloc.endDate).getTime()));
    const overlapMonths = Math.ceil((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60 * 24 * 30));
    return sum + (STANDARD_HOURS_PER_MONTH * overlapMonths * (alloc.allocationPercent ?? 0) / 100);
  }, 0);
  
  const availableHours = Math.max(0, totalCapacity - allocatedHours);
  const utilizationPercent = Math.round((allocatedHours / totalCapacity) * 100);
  
  // Determine availability status
  let status: AvailabilityStatus;
  if (utilizationPercent >= 100) {
    status = 'unavailable';
  } else if (utilizationPercent >= 70) {
    status = 'partially-available';
  } else {
    status = 'available';
  }
  
  // Map conflicts
  const conflicts: ProjectConflict[] = overlappingAllocations.map(alloc => {
    const allocPercent = alloc.allocationPercent ?? 0;
    let conflictLevel: ConflictLevel;
    if (allocPercent >= 80) {
      conflictLevel = 'high';
    } else if (allocPercent >= 50) {
      conflictLevel = 'medium';
    } else if (allocPercent >= 20) {
      conflictLevel = 'low';
    } else {
      conflictLevel = 'none';
    }
    
    return {
      projectId: alloc.projectId,
      projectName: alloc.projectName ?? `Project ${alloc.projectId}`,
      allocatedHours: alloc.allocatedHours ?? allocPercent,
      startDate: alloc.startDate,
      endDate: alloc.endDate,
      conflictLevel,
    };
  });
  
  return {
    status,
    availableHours,
    totalCapacity,
    utilizationPercent,
    conflicts,
  };
}

/**
 * Calculate skills match between person and required skills
 */
export function calculateSkillsMatch(
  personSkills: string[],
  requiredSkills: string[]
): { matches: SkillMatch[]; matchPercent: number } {
  if (!requiredSkills || requiredSkills.length === 0) {
    return { matches: [], matchPercent: 100 };
  }
  
  const matches: SkillMatch[] = requiredSkills.map(skill => ({
    skill,
    required: true,
    hasSkill: personSkills.some(ps => 
      ps.toLowerCase().includes(skill.toLowerCase()) || 
      skill.toLowerCase().includes(ps.toLowerCase())
    ),
  }));
  
  const matchedCount = matches.filter(m => m.hasSkill).length;
  const matchPercent = Math.round((matchedCount / requiredSkills.length) * 100);
  
  return { matches, matchPercent };
}

/**
 * Calculate suitability score for a person
 */
export function calculateSuitabilityScore(
  person: Employee,
  availability: PersonAvailability,
  skillsMatchPercent: number,
  requiredHours: number,
  roleName: string
): number {
  let score = 0;
  
  // Skills match (40% weight)
  score += skillsMatchPercent * 0.4;
  
  // Availability (30% weight)
  const availabilityScore = Math.min(100, (availability.availableHours / requiredHours) * 100);
  score += availabilityScore * 0.3;
  
  // Role match (20% weight)
  const roleMatch = person.role.toLowerCase().includes(roleName.toLowerCase()) ||
                    roleName.toLowerCase().includes(person.role.toLowerCase());
  score += (roleMatch ? 100 : 50) * 0.2;
  
  // Utilization (10% weight) - prefer people with moderate utilization
  const utilizationScore = availability.utilizationPercent < 50 ? 100 : 
                          availability.utilizationPercent < 80 ? 80 : 40;
  score += utilizationScore * 0.1;
  
  return Math.round(score);
}

/**
 * Generate rationale for recommendation
 */
export function generateRationale(
  person: Employee,
  availability: PersonAvailability,
  skillsMatchPercent: number,
  requiredHours: number,
  roleName: string
): string[] {
  const rationale: string[] = [];
  
  // Skills match
  if (skillsMatchPercent >= 90) {
    rationale.push(`Excellent skills match (${skillsMatchPercent}%)`);
  } else if (skillsMatchPercent >= 70) {
    rationale.push(`Good skills match (${skillsMatchPercent}%)`);
  } else if (skillsMatchPercent >= 50) {
    rationale.push(`Moderate skills match (${skillsMatchPercent}%)`);
  }
  
  // Availability
  if (availability.status === 'available') {
    rationale.push(`Fully available (${availability.availableHours}h capacity)`);
  } else if (availability.status === 'partially-available') {
    rationale.push(`Partially available (${availability.availableHours}h of ${requiredHours}h needed)`);
  }
  
  // Role match
  if (person.role.toLowerCase().includes(roleName.toLowerCase())) {
    rationale.push(`Exact role match: ${person.role}`);
  } else if (roleName.toLowerCase().includes(person.role.toLowerCase())) {
    rationale.push(`Related role: ${person.role}`);
  }
  
  // Experience indicators
  if (person.role.toLowerCase().includes('senior') || person.role.toLowerCase().includes('lead')) {
    rationale.push('Senior-level experience');
  }
  
  return rationale;
}

/**
 * Generate warnings for recommendation
 */
export function generateWarnings(
  availability: PersonAvailability,
  skillsMatchPercent: number,
  requiredHours: number
): string[] {
  const warnings: string[] = [];
  
  // Availability warnings
  if (availability.status === 'unavailable') {
    warnings.push('Currently fully allocated to other projects');
  } else if (availability.availableHours < requiredHours) {
    const shortfall = requiredHours - availability.availableHours;
    warnings.push(`${shortfall}h short of required hours`);
  }
  
  // Conflict warnings
  const highConflicts = availability.conflicts.filter(c => c.conflictLevel === 'high');
  if (highConflicts.length > 0) {
    warnings.push(`${highConflicts.length} high-priority project conflict(s)`);
  }
  
  // Skills warnings
  if (skillsMatchPercent < 50) {
    warnings.push('Limited skills match - may require training');
  }
  
  // Over-utilization warning
  if (availability.utilizationPercent >= 90) {
    warnings.push('Near capacity - limited flexibility');
  }
  
  return warnings;
}

/**
 * Generate recommendations for a role
 */
export function generateRecommendations(
  allPeople: Employee[],
  allocations: ResourceAllocation[],
  roleName: string,
  requiredHours: number,
  requiredSkills: string[],
  projectId: number,
  projectStartDate: string,
  projectEndDate: string
): PersonRecommendation[] {
  const recommendations: PersonRecommendation[] = allPeople
    .filter(person => person.isAvailable !== false) // Filter out explicitly unavailable people
    .filter(person => person.role === roleName) // Only show people with matching role
    .map(person => {
      const availability = calculatePersonAvailability(
        person,
        allocations,
        projectStartDate,
        projectEndDate
      );
      
      const { matches: skillsMatch, matchPercent: skillsMatchPercent } = calculateSkillsMatch(
        person.skills,
        requiredSkills
      );
      
      const suitabilityScore = calculateSuitabilityScore(
        person,
        availability,
        skillsMatchPercent,
        requiredHours,
        roleName
      );
      
      const rationale = generateRationale(
        person,
        availability,
        skillsMatchPercent,
        requiredHours,
        roleName
      );
      
      const warnings = generateWarnings(
        availability,
        skillsMatchPercent,
        requiredHours
      );
      
      return {
        person,
        availability,
        suitabilityScore,
        skillsMatch,
        skillsMatchPercent,
        rationale,
        warnings,
        rank: 0, // Will be set after sorting
      };
    })
    .sort((a, b) => b.suitabilityScore - a.suitabilityScore)
    .map((rec, index) => ({
      ...rec,
      rank: index + 1,
    }));
  
  return recommendations;
}

/**
 * Filter people based on criteria
 */
export function filterPeople(
  people: Employee[],
  searchQuery: string,
  roles: string[],
  departments: string[],
  availabilityStatuses: AvailabilityStatus[],
  availabilityMap: Map<number, PersonAvailability>
): Employee[] {
  return people.filter(person => {
    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        person.name.toLowerCase().includes(query) ||
        person.role.toLowerCase().includes(query) ||
        person.email.toLowerCase().includes(query) ||
        person.skills.some(skill => skill.toLowerCase().includes(query));
      
      if (!matchesSearch) return false;
    }
    
    // Role filter
    if (roles.length > 0 && !roles.includes(person.role)) {
      return false;
    }
    
    // Department filter
    if (departments.length > 0 && !departments.includes(person.department)) {
      return false;
    }
    
    // Availability filter
    if (availabilityStatuses.length > 0) {
      const availability = availabilityMap.get(person.id);
      if (!availability || !availabilityStatuses.includes(availability.status)) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Get unique values from array
 */
export function getUniqueValues<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

/**
 * Format hours for display
 */
export function formatHours(hours: number): string {
  return `${hours.toFixed(0)}h`;
}

/**
 * Get availability status color
 */
export function getAvailabilityColor(status: AvailabilityStatus): string {
  switch (status) {
    case 'available':
      return 'text-green-600';
    case 'partially-available':
      return 'text-amber-600';
    case 'unavailable':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

/**
 * Get availability status badge variant
 */
export function getAvailabilityBadgeVariant(status: AvailabilityStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'available':
      return 'default';
    case 'partially-available':
      return 'secondary';
    case 'unavailable':
      return 'destructive';
    default:
      return 'outline';
  }
}

/**
 * Get suitability score color
 */
export function getSuitabilityColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Get suitability score label
 */
export function getSuitabilityLabel(score: number): string {
  if (score >= 90) return 'Excellent Match';
  if (score >= 80) return 'Very Good Match';
  if (score >= 70) return 'Good Match';
  if (score >= 60) return 'Fair Match';
  return 'Poor Match';
}
